import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { TabBar, PillChip } from "@/components/toggles";
import { ArrowLeft, Plus, Trash2, Save, MapPin, CreditCard, Package, Check } from "lucide-react";

export default function BuyerSettingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [prefs, setPrefs] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("addresses");

  useEffect(() => {
    if (!user || user === false) { navigate("/login"); return; }
    load();
  }, [user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const [a, p, pr] = await Promise.all([api.get("/buyer/addresses"), api.get("/buyer/payment-methods"), api.get("/buyer/checkout-preferences")]);
      setAddresses(a.data.addresses || []);
      setPayments(p.data.payment_methods || []);
      setPrefs(pr.data || {});
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const deleteAddr = async (id) => { try { await api.delete(`/buyer/addresses/${id}`); load(); } catch { /* ignore */ } };
  const deletePay = async (id) => { try { await api.delete(`/buyer/payment-methods/${id}`); load(); } catch (e) { /* ignore */ } };
  const setDefaultAddr = async (id) => { try { await api.post("/buyer/addresses", { address_id: id, is_default: true }); load(); } catch { /* ignore */ } };
  const setDefaultPay = async (id) => { try { await api.post("/buyer/payment-methods", { method_id: id, is_default: true }); load(); } catch { /* ignore */ } };

  if (loading) return <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" /></div>;

  return (
    <div data-testid="buyer-settings-page">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E5E5E5]">
        <button onClick={() => navigate("/profile")}><ArrowLeft size={20} /></button>
        <h1 className="text-base font-bold">{t("buyer_settings.title")}</h1>
      </div>

      <TabBar
        tabs={[
          { key: "addresses", label: t("buyer_settings.tab_addresses"), testId: "buyer-settings-tab-addresses" },
          { key: "payments", label: t("buyer_settings.tab_payments"), testId: "buyer-settings-tab-payments" },
        ]}
        activeKey={tab}
        onChange={setTab}
      />

      {tab === "addresses" && (
        <div className="px-4 py-4 space-y-3">
          {addresses.length === 0 ? (
            <p className="text-sm text-[#999] py-8 text-center">{t("buyer_settings.no_addresses")}</p>
          ) : addresses.map((a) => (
            <div key={a.address_id} className="border border-[#E5E5E5] p-3 flex items-start gap-3">
              {a.address_kind === "PACKSTATION" ? <Package size={16} className="text-[#999] mt-0.5 shrink-0" /> : <MapPin size={16} className="text-[#999] mt-0.5 shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{a.name} {a.is_default && <span className="text-[10px] text-green-600">{t("buyer_settings.default")}</span>}</p>
                {a.address_kind === "PACKSTATION" ? (
                  <p className="text-xs text-[#999]">Packstation {a.packstation_number}, PostNr {a.post_number}, {a.postal_code} {a.city}</p>
                ) : (
                  <p className="text-xs text-[#999]">{a.street} {a.house_number}, {a.postal_code} {a.city}</p>
                )}
              </div>
              <div className="flex gap-1 shrink-0">
                {!a.is_default && <button onClick={() => setDefaultAddr(a.address_id)} className="h-7 px-2 border border-[#E5E5E5] text-[10px]" title={t("buyer_settings.set_default")}><Check size={10} /></button>}
                <button onClick={() => deleteAddr(a.address_id)} className="h-7 px-2 border border-red-200 text-red-500 text-[10px]"><Trash2 size={10} /></button>
              </div>
            </div>
          ))}
          <AddAddressForm onSaved={load} />
        </div>
      )}

      {tab === "payments" && (
        <div className="px-4 py-4 space-y-3">
          {payments.length === 0 ? (
            <p className="text-sm text-[#999] py-8 text-center">{t("buyer_settings.no_payments")}</p>
          ) : payments.map((p) => (
            <div key={p.method_id} className="border border-[#E5E5E5] p-3 flex items-center gap-3">
              <CreditCard size={16} className="text-[#999] shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium">{p.label} {p.is_default && <span className="text-[10px] text-green-600">{t("buyer_settings.default")}</span>}</p>
                <p className="text-xs text-[#999]">{p.payment_type}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                {!p.is_default && <button onClick={() => setDefaultPay(p.method_id)} className="h-7 px-2 border border-[#E5E5E5] text-[10px]"><Check size={10} /></button>}
                <button onClick={() => deletePay(p.method_id)} className="h-7 px-2 border border-red-200 text-red-500 text-[10px]"><Trash2 size={10} /></button>
              </div>
            </div>
          ))}
          <AddPaymentForm onSaved={load} />
        </div>
      )}
    </div>
  );
}

function AddAddressForm({ onSaved }) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [kind, setKind] = useState("STANDARD");
  const [f, setF] = useState({});
  const u = (k, v) => setF(p => ({ ...p, [k]: v }));

  const save = async () => {
    try {
      await api.post("/buyer/addresses", { address_kind: kind, ...f });
      setShow(false); setF({}); onSaved();
    } catch { /* ignore */ }
  };

  if (!show) return <button onClick={() => setShow(true)} className="w-full h-10 border border-dashed border-[#E5E5E5] text-sm text-[#999] flex items-center justify-center gap-1"><Plus size={14} /> {t("buyer_settings.add_address")}</button>;

  return (
    <div className="border border-[#E5E5E5] p-4 space-y-2">
      <div className="flex gap-2 mb-2">
        {[["STANDARD", t("buyer_settings.standard")], ["PACKSTATION", t("buyer_settings.packstation")]].map(([c, l]) => (
          <PillChip key={c} active={kind === c} onClick={() => setKind(c)} variant="neutral" size="md">{l}</PillChip>
        ))}
      </div>
      <input value={f.name || ""} onChange={(e) => u("name", e.target.value)} className="w-full rounded-full h-9 px-3 border border-[#E5E5E5] text-sm" placeholder="Name *" />
      {kind === "STANDARD" ? (<>
        <input value={f.street || ""} onChange={(e) => u("street", e.target.value)} className="w-full rounded-full h-9 px-3 border border-[#E5E5E5] text-sm" placeholder={`${t("buyer_settings.street")} *`} />
        <div className="flex gap-2">
          <input value={f.postal_code || ""} onChange={(e) => u("postal_code", e.target.value)} className="w-24 rounded-full h-9 px-3 border border-[#E5E5E5] text-sm" placeholder={`${t("buyer_settings.postal_code")} *`} />
          <input value={f.city || ""} onChange={(e) => u("city", e.target.value)} className="flex-1 rounded-full h-9 px-3 border border-[#E5E5E5] text-sm" placeholder={`${t("buyer_settings.city")} *`} />
        </div>
      </>) : (<>
        <input value={f.packstation_number || ""} onChange={(e) => u("packstation_number", e.target.value)} className="w-full rounded-full h-9 px-3 border border-[#E5E5E5] text-sm" placeholder={`${t("buyer_settings.packstation_no")} *`} />
        <input value={f.post_number || ""} onChange={(e) => u("post_number", e.target.value)} className="w-full rounded-full h-9 px-3 border border-[#E5E5E5] text-sm" placeholder={`${t("buyer_settings.post_no")} *`} />
        <div className="flex gap-2">
          <input value={f.postal_code || ""} onChange={(e) => u("postal_code", e.target.value)} className="w-24 rounded-full h-9 px-3 border border-[#E5E5E5] text-sm" placeholder={`${t("buyer_settings.postal_code")} *`} />
          <input value={f.city || ""} onChange={(e) => u("city", e.target.value)} className="flex-1 rounded-full h-9 px-3 border border-[#E5E5E5] text-sm" placeholder={`${t("buyer_settings.city")} *`} />
        </div>
      </>)}
      <div className="flex gap-2">
        <button onClick={() => setShow(false)} className="rounded-full h-9 px-4 border border-[#E5E5E5] text-xs text-[#666]">{t("buyer_settings.cancel")}</button>
        <button onClick={save} className="h-9 px-4 bg-[#0A0A0A] text-white text-xs font-medium flex items-center gap-1"><Save size={12} /> {t("buyer_settings.save")}</button>
      </div>
    </div>
  );
}

function AddPaymentForm({ onSaved }) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const save = async (type) => {
    try { await api.post("/buyer/payment-methods", { payment_type: type, label: type === "PAYPAL" ? "PayPal" : t("buyer_settings.bank_prepay") }); setShow(false); onSaved(); } catch { /* ignore */ }
  };
  if (!show) return <button onClick={() => setShow(true)} className="w-full h-10 border border-dashed border-[#E5E5E5] text-sm text-[#999] flex items-center justify-center gap-1"><Plus size={14} /> {t("buyer_settings_extra.add_payment")}</button>;
  return (
    <div className="border border-[#E5E5E5] p-4 space-y-2">
      <p className="text-xs text-[#999] uppercase tracking-wider font-medium">{t("buyer_settings_extra.choose_payment")}</p>
      <button onClick={() => save("PAYPAL")} className="w-full h-10 border border-[#E5E5E5] text-sm flex items-center gap-2 px-3 hover:bg-[#FAFAFA]"><CreditCard size={14} /> PayPal</button>
      <button onClick={() => save("BANK_TRANSFER")} className="w-full h-10 border border-[#E5E5E5] text-sm flex items-center gap-2 px-3 hover:bg-[#FAFAFA]"><CreditCard size={14} /> {t("buyer_settings.bank_prepay")}</button>
      <button onClick={() => setShow(false)} className="text-xs text-[#999]">{t("buyer_settings.cancel")}</button>
    </div>
  );
}
